/**
 * VG2 · Anon axios factory — shared helper.
 *
 * Every VG2 helper (`vg2Journey`, `vg2Valuta`, `vg2Decidi`, `vg2Agisci`,
 * `vg2Documents`, `vg2ValutaQuestionnaire`, `vg2PaRequest`) authenticates
 * anonymous callers via the `X-VG2-Anon-Token` header, backed by a token
 * cached in `localStorage` under `vg2_anon_token`.
 *
 * Historically each helper only wired a *request* interceptor. When the
 * cached token got orphaned (DB reset / redeploy / manual purge), every
 * request in the funnel started returning `401 Invalid or expired
 * anonymous session` with no self-heal — the owner saw a generic
 * "Non riesco ad avviare il percorso" message and could only recover by
 * clearing localStorage manually.
 *
 * `createAnonAxios(baseURL)` returns an axios instance wired with:
 *   1. Request interceptor: attaches the cached anon token, minting a
 *      new session on the fly if none is cached.
 *   2. Response interceptor: on `401` with `"Invalid or expired
 *      anonymous session"`, evicts the stale token, mints a fresh one
 *      and RETRIES the original request exactly once. If the retry
 *      still fails, the error bubbles up unchanged.
 *
 * The retry is bounded (max one per request) and marked with a
 * dedicated header so we can't ever loop. The token-mint side-channel
 * uses a bare axios (no interceptor) so it can't trigger nested retries.
 */
import axios from "axios";

export const LS_ANON = "vg2_anon_token";
const API_V2_SUFFIX = "/api/v2";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_API_V2 = `${BACKEND_URL}${API_V2_SUFFIX}`;

// A separate bare axios used ONLY to mint a new anon session — never
// carries interceptors, so retries can't ricochet through it.
const bareAxios = axios.create();

/** Mint a fresh anonymous session and cache the token. */
export async function mintAnonSession(baseURL = DEFAULT_API_V2) {
  const { data } = await bareAxios.post(`${baseURL}/anon-sessions`);
  if (data && data.token) {
    localStorage.setItem(LS_ANON, data.token);
    return data.token;
  }
  throw new Error("mintAnonSession: server did not return a token");
}

/** Read the cached token, or mint one on the fly if missing. */
export async function ensureAnonSession(baseURL = DEFAULT_API_V2) {
  const t = localStorage.getItem(LS_ANON);
  if (t) return t;
  return await mintAnonSession(baseURL);
}

/** True when the backend reports the anon token is invalid/expired. */
function isStaleAnonSession401(err) {
  if (!err || !err.response || err.response.status !== 401) return false;
  const detail = err.response.data && err.response.data.detail;
  const msg = typeof detail === "string"
    ? detail
    : (detail && detail.message) || "";
  // Matches both "Invalid or expired anonymous session" (v2_cases) and
  // any future 401 that carries the "anonymous" marker in the detail.
  return /anonymous session|anon.*token/i.test(msg || "");
}

// Decode a JWT payload (no verification) — used only to drop a client-side
// expired token so the public funnel never breaks on a stale credential.
function _jwtExpired(token) {
  try {
    const part = token.split(".")[1];
    const json = JSON.parse(
      decodeURIComponent(
        atob(part.replace(/-/g, "+").replace(/_/g, "/"))
          .split("").map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2)).join("")
      )
    );
    if (!json || !json.exp) return false;
    return Date.now() >= json.exp * 1000;
  } catch {
    return false;
  }
}

function _storedRole() {
  try {
    return (JSON.parse(localStorage.getItem("vg2_user") || "null") || {}).ruolo || null;
  } catch {
    return null;
  }
}

/**
 * Create an axios instance that handles anon-token attach + auto-retry.
 *
 * @param {string} [baseURL] override; defaults to `${BACKEND}/api/v2`.
 * @returns {import("axios").AxiosInstance}
 */
export function createAnonAxios(baseURL = DEFAULT_API_V2) {
  const instance = axios.create({ baseURL });

  // --- Request: attach token (mint if missing on first call) ------------
  instance.interceptors.request.use((config) => {
    const t = localStorage.getItem(LS_ANON);
    if (t) config.headers["X-VG2-Anon-Token"] = t;
    // §I-2.5 · Forward the JWT only for the OWNER role, so a PA/other
    // role using the public funnel never creates role-owned cases and
    // the PA flow is untouched. A stale (expired) token is dropped from
    // the browser and NOT sent, so a dropped session can't block the
    // funnel nor spawn a parallel anon case.
    const jwt = localStorage.getItem("vg2_token");
    if (jwt) {
      if (_jwtExpired(jwt)) {
        localStorage.removeItem("vg2_token");
        localStorage.removeItem("vg2_user");
      } else if (_storedRole() === "owner") {
        config.headers["Authorization"] = `Bearer ${jwt}`;
      }
    }
    return config;
  });

  // --- Response: on stale-anon 401, refresh & retry ONCE ---------------
  instance.interceptors.response.use(
    (resp) => resp,
    async (err) => {
      const original = err.config || {};
      const alreadyRetried = original.__vg2_anon_retried === true;
      if (alreadyRetried || !isStaleAnonSession401(err)) {
        return Promise.reject(err);
      }
      // Evict the stale token and mint a new one.
      localStorage.removeItem(LS_ANON);
      try {
        await mintAnonSession(baseURL);
      } catch (mintErr) {
        return Promise.reject(mintErr);
      }
      // Retry the original request exactly once. We rebuild the headers
      // dict so the new token overrides the stale one carried in
      // `original.headers`.
      original.__vg2_anon_retried = true;
      original.headers = { ...(original.headers || {}) };
      delete original.headers["X-VG2-Anon-Token"];
      // The request interceptor will re-attach the fresh token.
      return instance.request(original);
    },
  );

  return instance;
}
